import type { TGouvFeatureDto, TGovernmentAddressSuggestion } from "@/api/gouv/search/gouv-address-search.type.ts";
import type { IExternalAddress } from "@/domains/address/external-address.ts";
import type {
  IAddressProviderVisitor,
  TAddressProviderVisitorOutput,
} from "@/domains/address/providers/address-provider-visitor.ts";
import type { IAddressProvider } from "@/domains/address/providers/address-provider.ts";

import { normalizeAxiosError } from "@/api/api-error-normalization.ts";
import GouvAddressApi from "@/api/gouv/gouv-address-api.ts";
import { EGouvSearchAddressType } from "@/api/gouv/search/gouv-address-search.type.ts";
import { FrenchGovernmentAddress } from "@/domains/address/providers/french-government/french-government-address.ts";
import { chain } from "@/lib/chain.ts";
import { captureNonBlockingError } from "@/lib/observability.ts";
import { isDefined } from "@/types/utils.ts";

const types = [EGouvSearchAddressType.HOUSENUMBER, EGouvSearchAddressType.STREET];

export const FrenchGovernmentAddressProvider: IAddressProvider = {
  accept<V extends IAddressProviderVisitor>(visitor: V): TAddressProviderVisitorOutput<V> {
    return visitor.visitFrenchGovernment() as TAddressProviderVisitorOutput<V>;
  },

  async search(query: string): Promise<Array<IExternalAddress<TGouvFeatureDto>>> {
    if (query.length < 3) {
      return [];
    }

    const autocompleteResults = await Promise.allSettled(
      types.map((type) => {
        return GouvAddressApi.autocompleteAddress({
          queries: {
            type: type,
            q: query,
            autocomplete: 1,
          },
        });
      }),
    );

    let hasReportedFailure = false;
    const results = autocompleteResults
      .flatMap((result) => {
        if (result.status === "rejected") {
          const error = normalizeAxiosError(result.reason, "api-gouv");
          if (!hasReportedFailure) {
            hasReportedFailure = true;
            captureNonBlockingError(error);
          }
          return null;
        }

        return result.value.features;
      })
      .filter(isDefined);

    return chain(results)
      .uniqBy((s) => s.properties.id)
      .sortBy((s) => -s.properties.score)
      .map((feature) => new FrenchGovernmentAddress(feature))
      .value();
  },
};

export function gouvFeatureToAddressSuggestion(feature: TGouvFeatureDto): TGovernmentAddressSuggestion {
  return {
    id: feature.properties.id,
    rawAddress: feature.properties.label,
    streetNumber: feature.properties.housenumber ?? null,
    street: feature.properties.street ?? "",
    complement: null,
    postalCode: feature.properties.postcode,
    city: feature.properties.city,
    latitude: feature.geometry.coordinates[1],
    longitude: feature.geometry.coordinates[0],
    cityCode: feature.properties.citycode,
    context: feature.properties.context,
    score: feature.properties.score,
    administrativeDivisions: feature.properties.context
      .split(",")
      .map((s) => s.trim())
      .slice(1),
    label: feature.properties.label,
  };
}
